import { useEffect, ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { LogOut, Building2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { identifyPosthogUser, resetPosthogIdentity } from '@/utils/posthogIdentity';

interface SuperAdminLayoutProps {
    children: ReactNode;
}

export default function SuperAdminLayout({ children }: SuperAdminLayoutProps) {
    const navigate = useNavigate();

    useEffect(() => {
        const check = async () => {
            try {
                const resp = await apiFetch('/api/auth/superadmin/me');
                if (!resp.ok) {
                    resetPosthogIdentity();
                    navigate({ to: '/superadmin-login' });
                    return;
                }
                const admin = await resp.json();
                identifyPosthogUser({
                    id: admin?.id,
                    email: admin?.email,
                    full_name: admin?.full_name,
                    is_superuser: true,
                });
            } catch {
                resetPosthogIdentity();
                navigate({ to: '/superadmin-login' });
            }
        };
        check();
    }, [navigate]);

    const handleLogout = async () => {
        try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch {}
        resetPosthogIdentity();
        navigate({ to: '/superadmin-login' });
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-blue-600" />
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">CafeMagic</h1>
                            <p className="text-sm text-slate-600">Tenant Administration</p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleLogout}
                        className="gap-2"
                    >
                        <LogOut className="h-4 w-4" />
                        Logout
                    </Button>
                </div>
            </header>

            {/* Navigation */}
            <nav className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex gap-8">
                        <a
                            href="/superadmin/tenants"
                            className="px-4 py-3 text-sm font-medium text-slate-700 hover:text-slate-900 border-b-2 border-transparent hover:border-blue-600 transition-colors"
                        >
                            Tenants
                        </a>
                        <a
                            href="/superadmin/superadmins"
                            className="px-4 py-3 text-sm font-medium text-slate-700 hover:text-slate-900 border-b-2 border-transparent hover:border-blue-600 transition-colors"
                        >
                            Superadmins
                        </a>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {children}
            </main>
        </div>
    );
}
